/**
 * SignMessage — EIP-191 personal_sign UI.
 *
 * Features: message input, sign button, signature display, verify round-trip.
 */

import { useState, useCallback, type ReactNode } from 'react';
import { useWallet } from '../WalletContext.js';
import { ethers } from 'ethers';

export function SignMessage({ onBack }: { onBack: () => void }): ReactNode {
  const { actions, status } = useWallet();
  const [message, setMessage] = useState('');
  const [signature, setSignature] = useState('');
  const [verified, setVerified] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const address = status.state === 'unlocked' ? status.address : '';

  const handleSign = useCallback(async () => {
    if (!message.trim()) {
      setError('Enter a message to sign');
      return;
    }

    setError('');
    setSignature('');
    setVerified(null);
    setLoading(true);

    try {
      const sig = await actions.signMessage(message);
      setSignature(sig);

      // Verify the signature immediately
      const recovered = ethers.verifyMessage(message, sig);
      setVerified(recovered.toLowerCase() === address.toLowerCase());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signing failed');
    } finally {
      setLoading(false);
    }
  }, [message, actions, address]);

  const handleCopySignature = async () => {
    if (signature) {
      await navigator.clipboard.writeText(signature);
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <h2>Sign Message</h2>
      </div>
      <div className="screen-body">
        <p className="text-secondary">
          Sign a message with your wallet&apos;s private key using EIP-191 (personal_sign).
        </p>

        <div className="form-group">
          <label>Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Enter a message to sign..."
            rows={4}
            autoFocus
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          className="btn-primary btn-full"
          onClick={() => void handleSign()}
          disabled={loading || !message.trim()}
        >
          {loading ? 'Signing...' : 'Sign Message'}
        </button>

        {signature && (
          <div className="signature-result">
            <div className="form-group">
              <label>
                Signature
                {verified === true && <span className="badge badge-success">Verified</span>}
                {verified === false && <span className="badge badge-error">Invalid</span>}
              </label>
              <div className="signature-display">
                <code className="signature-hex">{signature}</code>
              </div>
              <button className="btn-ghost btn-sm" onClick={() => void handleCopySignature()}>
                Copy Signature
              </button>
            </div>

            <div className="verify-info">
              <p className="text-sm text-muted">
                Signer: {address}
              </p>
              <p className="text-sm text-muted">
                Signature length: {(signature.length - 2) / 2} bytes
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
